import { LOCALSTORAGE_KEYS } from "../constants";
import type { LocalStorageApi } from "./shared";

export * from "./shared";

/**
 * Web/electron half — the browser's synchronous `localStorage`. Verbatim from
 * `apps/web/src/utils/localStorage.js`; the bodies are one-liners and stay that way.
 *
 * Note web takes no `isBoolean` parameter and has no `isOffline` special case — it always
 * returns the raw `string | null` the browser gives back. The parameter is accepted and
 * IGNORED so the two platforms share one call signature (`LocalStorageApi`); adding a real
 * boolean coercion here would change web's behaviour, which this migration does not do.
 */
export const setLocalStorage: LocalStorageApi["setLocalStorage"] = (key, value) => {
  localStorage.setItem(key, value);
};

/**
 * ⚠️ Deliberately NOT annotated with `LocalStorageApi["getLocalStorage"]`. That contract types
 * the return as the UNION of both platforms (`string | boolean | null | undefined`) so shared
 * consumers must handle both — but web callers have always received `string | null`, and
 * widening it here broke 25 call sites at `tsc` (`Argument of type 'string | true'...`).
 * Each platform file keeps its own precise return type; the union is for shared code only.
 */
export const getLocalStorage = (key: string, _isBoolean?: boolean): string | null => {
  return localStorage.getItem(key);
};

export const removeLocalStorage: LocalStorageApi["removeLocalStorage"] = (key) => {
  localStorage.removeItem(key);
};

export const clearLocalStorage: LocalStorageApi["clearLocalStorage"] = () => {
  localStorage.clear();
};

/**
 * Reads a key and JSON-parses it, with no try/catch — verbatim from web.
 *
 * ⚠️ **Web-only, and it is one of the ASYMMETRIC exports of this folder.** Mobile has its own
 * `getLocalStorageWithParse` (`strVal ? JSON.parse(strVal) : ""`), which returns `""` for a
 * missing key where this returns `null` (`JSON.parse(null)` is `null`). Only web's consumer
 * — `helpers/adhocTableHelpers`, which does `|| []` on the result — needed uplifting, so
 * mobile's copy stays in the app and is NOT mirrored here. A `shared.ts` must therefore never
 * import this bare; a `.desktop.ts` may.
 */
export const getLocalStorageWithParse = (key: string): any => {
  return JSON.parse(localStorage.getItem(key) as string);
};

/**
 * The USB printer this terminal has configured, or `{}` when there is none.
 *
 * Uplifted 2026-09-11 for the item-label-settings flow, whose printer picker needs the full
 * device printer set. **Byte-identical to mobile's** apart from quote style, but it lives in the
 * platform files rather than `shared.ts` for the reason this folder's `shared.ts` records: it
 * calls `getLocalStorage`, and a `shared.ts` importing its own folder index would be circular.
 * Both apps now re-export from here.
 */
export const USBPrinter = (): any => {
  return JSON.parse(getLocalStorage(LOCALSTORAGE_KEYS.usbPrinter) || "{}");
};

/**
 * Bluetooth printers paired with this terminal.
 *
 * ⚠️ The stored records key their identity as `inner_mac_address`; every consumer in the app
 * addresses a printer by `mid`, so the mapping here is load-bearing, not cosmetic. Same reason
 * `printerId` is duplicated onto it.
 */
export const getAddedBlePrintersList = (): any[] => {
  const blePrinterList = JSON.parse(
    getLocalStorage(LOCALSTORAGE_KEYS.blePrinterList) || "[]"
  );
  return blePrinterList.map((printerObj: any) => ({
    ...printerObj,
    mid: printerObj.inner_mac_address,
    printerId: printerObj.inner_mac_address,
  }));
};
